/* Copyright (c) 2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  ApolloProvider,
  from,
  ServerError,
  ServerParseError,
  Operation,
  NextLink,
  Observable,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { ExecutionResult, GraphQLError } from "graphql";
import { auth0Client } from "../Auth0";

type GraphQLErrorCustom = GraphQLError & {
  errorType?: string;
};

type ErrorResponseCustom = {
  graphQLErrors?: ReadonlyArray<GraphQLErrorCustom>;
  networkError?: Error | ServerError | ServerParseError;
  response?: ExecutionResult;
  operation: Operation;
  forward: NextLink;
};

async function getNewToken() {
  await auth0Client.checkSession({ ignoreCache: true });
  const token = await auth0Client.getIdTokenClaims();
  return token.__raw;
}

const errorLink = onError(
  ({ graphQLErrors, operation, forward }: ErrorResponseCustom) => {
    if (graphQLErrors != null) {
      // eslint-disable-next-line fp/no-loops
      for (const err of graphQLErrors) {
        if (err?.errorType === "UnauthorizedException") {
          return new Observable((observer) => {
            getNewToken()
              .then(async (token) => {
                const oldHeaders = operation.getContext().headers;
                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    Authorization: token,
                  },
                });

                return undefined;
              })
              .then(() => {
                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                };

                // Retry last failed request
                return forward(operation).subscribe(subscriber);
              })
              .catch((error) => {
                observer.error(error);
              });
          });
        }
      }
    }
  }
);

function customFetch(uri: RequestInfo, options: RequestInit) {
  const { operationName = "" } = JSON.parse(options.body?.toString() ?? "{}");

  const fetchUrl = `${uri}?opname=${operationName}`;
  return fetch(fetchUrl, options);
}

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
  fetch: customFetch,
});

const httpAdminLink = new HttpLink({
  uri: process.env.REACT_APP_ADMIN_GRAPHQL_ENDPOINT,
  fetch: customFetch,
});

const authLink = setContext(async (_, { headers }) => {
  try {
    // Get saved token or refresh it in case it expired
    const token = await auth0Client.getIdTokenClaims();

    return {
      headers: {
        ...headers,
        ...(token != null && { Authorization: token.__raw }),
      },
    };
  } catch {
    window.location.reload();
  }
});

const retryLink = new RetryLink();

export const client = new ApolloClient({
  cache: new InMemoryCache({}),
  link: from([authLink, errorLink, retryLink, httpLink]),
  defaultOptions: {
    query: {
      errorPolicy: "all",
    },
  },
});

const adminClient = new ApolloClient({
  cache: new InMemoryCache({}),
  link: from([authLink, errorLink, retryLink, httpAdminLink]),
  defaultOptions: {
    query: {
      errorPolicy: "all",
    },
  },
});

export function CustomApolloProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return <ApolloProvider client={adminClient}>{children}</ApolloProvider>;
}
